import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/axios";
import LoadingScreen from "../../components/LoadingScreen";
import { useSnackbar } from "../../utils/SnackbarProvider";

/* ── Animation keyframes ── */
const animStyles = `
@keyframes cardFadeIn {
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
}
`;

const VendorUnansweredQuestions = ({ onAnswerSubmitted }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Fetch unanswered questions
  const fetchUnansweredQuestions = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/vendor/dashboard/unanswered-questions");
      setQuestions(data);
    } catch (err) {
      console.error("Failed to fetch unanswered questions", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnansweredQuestions();
  }, []);

  // Handle answer submission
  const handleAnswerSubmit = async () => {
    if (!answerText.trim() || !selectedQuestion) {
      showSnackbar("Please enter an answer", "warning");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/products/questions/${selectedQuestion._id}/answers`, {
        answerText,
      });
      // Refresh the list
      fetchUnansweredQuestions();
      // Notify parent to refresh count
      if (onAnswerSubmitted) {
        onAnswerSubmitted();
      }
      setOpenDialog(false);
      setAnswerText("");
      setSelectedQuestion(null);
      showSnackbar("Answer posted successfully!", "success");
    } catch (err) {
      console.error("Failed to submit answer", err);
      showSnackbar(
        err.response?.data?.message || "Failed to post answer. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openAnswerDialog = (question) => {
    setSelectedQuestion(question);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuestion(null);
    setAnswerText("");
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <style>{animStyles}</style>

      {/* ================= HEADER ================= */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #0062ff 100%)', px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button onClick={() => navigate("/vendor/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 0.5, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
              <ArrowBack sx={{ fontSize: 16 }} />
              Back to Dashboard
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>❓ Unanswered Questions</Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Respond to customer inquiries about your products.</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        {/* ================= Section Header ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#ff9900' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Questions Awaiting Response ({questions.length})
          </Typography>
        </Box>

        {/* Loading State */}
        {loading && <LoadingScreen message="Loading unanswered questions..." />}

        {/* No Questions */}
        {!loading && questions.length === 0 && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <QuestionAnswerIcon sx={{ fontSize: 48, color: "textSecondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary">No unanswered questions</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Great job! All customer questions have been answered.</Typography>
          </Paper>
        )}

        {/* Questions Grid - 2 columns on desktop */}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(2, 1fr)',
            },
            alignItems: 'stretch',
          }}
        >
          {questions.map((question) => (
            <Card
              key={question._id}
              sx={{
                borderRadius: '12px',
                border: '1.5px solid #f1f5f9',
                boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
                transition: 'transform 0.18s ease, boxShadow 0.18s ease',
                animation: 'cardFadeIn 0.35s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' },
              }}
            >
              {/* Question Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" display="block">Question</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      mt: 0.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      color: "#1e293b"
                    }}
                  >
                    {question.questionText}
                  </Typography>
                </Box>
                <Chip
                  label="Pending"
                  size="small"
                  sx={{
                    backgroundColor: "#ff9900",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    flexShrink: 0,
                  }}
                />
              </Box>

              <CardContent sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {/* Product Info */}
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>Product</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1976d2" }}>
                      {question.product?.title || "Product"}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Metadata */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">Asked On</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(question.createdAt).toLocaleDateString("en-IN", {
                          year: "numeric", month: "short", day: "numeric",
                        })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">Customer</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {question.userId?.name || "Anonymous"}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Action Button */}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => openAnswerDialog(question)}
                    sx={{
                      backgroundColor: "#ff9900",
                      color: "white",
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#e68a00",
                      },
                    }}
                  >
                    Write Answer
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Answer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={700}>Answer Customer Question</Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedQuestion && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>Product</Typography>
                <Chip
                  label={selectedQuestion.product?.title || "Unknown Product"}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>Customer Question</Typography>
                <Paper sx={{ p: 2, bgcolor: "#f5f5f5", borderLeft: "4px solid #ff9900" }}>
                  <Typography variant="body2">
                    {selectedQuestion.questionText}
                  </Typography>
                </Paper>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>Your Answer</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Write a helpful and accurate answer..."
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAnswerSubmit}
            disabled={!answerText.trim() || submitting}
            sx={{
              backgroundColor: "#ff9900",
              "&:hover": {
                backgroundColor: "#e68a00",
              },
            }}
          >
            {submitting ? "Posting..." : "Post Answer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorUnansweredQuestions;